import { Field } from 'react-final-form';
import { ChangeEvent, useCallback, useState, FocusEvent } from 'react';

import { IParticipantPosition } from 'APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IAutoCompleteItem } from 'UIPackage/MainModule/interfaces/IAutoCompleteItem';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

interface IPmResultOfInspectionsResultDecisionsOfficialsProps {
  formRef?: IKnmFormRef;
  lateFieldName: string;
  name: string;
  pmResultDecisionId: string;
  positionsSelectOptions?: IDictionarySelectOption<IParticipantPosition>[];
  watchOnly?: boolean;
}

export const PmResultOfInspectionsResultDecisionsSigner = ({
  formRef,
  lateFieldName,
  name,
  pmResultDecisionId,
  positionsSelectOptions,
  watchOnly,
}: IPmResultOfInspectionsResultDecisionsOfficialsProps) => {
  const [fullNameOptions, setFullNameOptions] = useState<Array<IAutoCompleteItem> | undefined>();

  const handleFullNameChange = useCallback(
    (value: string) => {
      if (value) {
        setFullNameOptions(
          formRef?.current
            .getFieldState('inspectorsErknm')
            ?.value?.filter(item => item.fullName?.toLowerCase().startsWith(value.toLowerCase()))
            .map(item => ({ label: item.fullName ?? '', value: item.fullName ?? '' })),
        );
      }
    },
    [formRef],
  );

  return (
    <Row>
      <FieldBlock
        label="Должностное лицо контрольного (надзорного) органа, подписавшее (осуществившее) решение"
        size="half"
        type="flex-label"
        id={`${pmResultDecisionId}.signer`}
      />

      <Row notWrap>
        <FieldBlock margin="right" id={`${pmResultDecisionId}.signerFullName`}>
          <Field name={`${name}.signerFullName`}>
            {({ input, meta }) => {
              function handleTextInputBlur(e: FocusEvent<HTMLInputElement>) {
                input.onBlur(e);
                const tagName = (e?.relatedTarget as Element)?.tagName;

                if (tagName !== 'UL') {
                  setFullNameOptions(undefined);
                }
              }

              function handleAutoCompleteChange(e: ChangeEvent<HTMLInputElement>) {
                const { value } = e.target;

                input.onChange(value);
                handleFullNameChange(value);
              }

              function handleAutoCompleteSelect(value: string) {
                return () => {
                  input.onChange(value);
                  const inspector = formRef?.current
                    .getFieldState('inspectorsErknm')
                    ?.value?.find(knmInspector => knmInspector.fullName === value);

                  setFullNameOptions(undefined);
                  if (inspector?.position) {
                    formRef?.current.change(`${name}.signerPosition` as keyof IKnmForm, inspector.position);
                  }
                };
              }

              const error = meta.touched && meta.error ? meta.error : undefined;

              return (
                <>
                  <AutoComplete
                    options={fullNameOptions}
                    noFoundMessage="Значение отсутствует"
                    handleAutoCompleteSelect={handleAutoCompleteSelect}
                  >
                    <TextInput
                      {...input}
                      id={input.name}
                      placeholder="Введите ФИО должностного лица"
                      error={error}
                      disabled={watchOnly}
                      onChange={handleAutoCompleteChange}
                      onBlur={handleTextInputBlur}
                    />
                  </AutoComplete>
                </>
              );
            }}
          </Field>
        </FieldBlock>
        <KnmLateFieldsRemark
          lateFieldPath={`${lateFieldName}/fioSigner`}
          fieldId={`${pmResultDecisionId}.signerFullName`}
        />
        <FieldBlock id={`${pmResultDecisionId}.signerPosition`}>
          <SelectInputField
            disabled={watchOnly}
            name={`${name}.signerPosition`}
            placeholder="Выберите должность"
            options={positionsSelectOptions}
            isSearchable
          />
        </FieldBlock>
        <KnmLateFieldsRemark
          lateFieldPath={`${lateFieldName}/titleSigner`}
          fieldId={`${pmResultDecisionId}.signerPosition`}
          isLeftPosition={true}
        />
      </Row>
    </Row>
  );
};
